import { useRoute, RouteProp } from "@react-navigation/native";
import {
  TICKET_LIST_QUERY,
  TICKET_READ_QUERY,
  useTicketReadQuery,
  useTicketScanByOrderIdMutation,
  useTicketUpdateMutation,
} from "@ticketingplatform/api/dist/ticket";
import { RootNavigationParamList } from "navigation/RootNavigation/types";
import { SCREENS } from "navigation/consts";
import { useCallback } from "react";
import { TicketStatus } from "@ticketingplatform/api/dist/__generated__/globalTypes";
import {
  GET_EVENT_OCCURRENCES,
  GET_EVENT_OCCURRENCE,
} from "@ticketingplatform/api/dist/event";

import { UseTicketStatusReturnType } from "./types";

export const useTicketDetailsRouteParams = () => {
  const { params } =
    useRoute<
      RouteProp<RootNavigationParamList, typeof SCREENS.TicketDetails>
    >();

  return params;
};

export const useTicket = () => {
  const { ticketId } = useTicketDetailsRouteParams();
  const { data, loading } = useTicketReadQuery({
    variables: {
      id: +ticketId,
      orderInclude: true,
      ticketPoolInclude: true,
      eventInclude: true,
    },
  });

  return {
    ticket: data?.ticket,
    loading,
  };
};

export const useTicketStatus = (
  ticketId: number,
  orderId?: number,
  eventOccurrenceId?: string
): UseTicketStatusReturnType => {
  const [ticketUpdate, { loading: singleLoading }] = useTicketUpdateMutation({
    evictQueries: [GET_EVENT_OCCURRENCES, GET_EVENT_OCCURRENCE],
  });

  const [ticketScanByOrderId, { loading: multiLoading }] =
    useTicketScanByOrderIdMutation({
      evictQueries: [
        GET_EVENT_OCCURRENCES,
        GET_EVENT_OCCURRENCE,
        TICKET_LIST_QUERY,
      ],
      refetchQueries: [TICKET_READ_QUERY],
      awaitRefetchQueries: true,
    });

  const setTicketStatusNew = useCallback(async () => {
    await ticketUpdate({
      variables: {
        id: +ticketId,
        input: {
          status: TicketStatus.NEW,
        },
      },
    });
  }, [ticketId, ticketUpdate]);

  const setTicketStatusScanned = useCallback(async () => {
    await ticketUpdate({
      variables: {
        id: +ticketId,
        input: {
          status: TicketStatus.SCANNED,
        },
      },
    });
  }, [ticketId, ticketUpdate]);

  const setAllTicketStatusScanned = useCallback(async () => {
    if (!orderId || !eventOccurrenceId) {
      return;
    }

    await ticketScanByOrderId({
      variables: {
        orderId,
        eventOccurrenceId,
      },
    });
  }, [eventOccurrenceId, orderId, ticketScanByOrderId]);

  return {
    setTicketStatusNew,
    setTicketStatusScanned,
    setAllTicketStatusScanned,
    loading: singleLoading || multiLoading,
  };
};
